import { ROUTERS, UploadExcelError } from '@/constant';
import { PayloadAction } from '@reduxjs/toolkit';
import { put, select } from 'redux-saga/effects';

import * as API from 'services';

import { MyPageSelectors } from '../my-page';
import {
	formatUploadExcelApplicantFailedList,
	formatWorkerApplicantAssignmentList,
	formatWorkerApplicantDetail,
	formatWorkerApplicantList,
	formatWorkerApplicantLodging,
	formatWorkerApplicantProcessInfoList,
	formatWorkerApplicantProgramList,
	formatWorkerApplicantRecommender,
	formatWorkerExcelApplicantList,
} from './worker-applicant.helper';
import { WorkerApplicantActions } from './worker-applicant.redux';
import {
	IPayloadApplicantCardRequest,
	IPayloadCreateWorkerApplicantLodgingRequest,
	IPayloadCreateWorkerApplicantProgramRequest,
	IPayloadCreateWorkerApplicantRequest,
	IPayloadDeleteWorkerApplicantProcessInfoRequest,
	IPayloadDeleteWorkerApplicantRequest,
	IPayloadGetWorkerApplicantAssignmentListRequest,
	IPayloadGetWorkerApplicantDepartureDateRequest,
	IPayloadGetWorkerApplicantDepartureDateResponse,
	IPayloadGetWorkerApplicantDetailRequest,
	IPayloadGetWorkerApplicantExcelListRequest,
	IPayloadGetWorkerApplicantListRequest,
	IPayloadGetWorkerApplicantProcessInfoListRequest,
	IPayloadGetWorkerApplicantProgramListRequest,
	IPayloadUpdateExtensionPeriodRequest,
	IPayloadUpdateStatusWorkerApplicantRequest,
	IPayloadUpdateWorkerApplicantRecommenderRequest,
	TPayloadUploadExcelApplicantRequest,
} from './worker-applicant.type';

export function* getWorkerApplicantListSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantListRequest>
) {
	const { callback, pathname, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantListResponse> =
			pathname === ROUTERS.ED1_01
				? yield API.getWorkerDashboardList({ ...rest })
				: yield API.getWorkerApplicantList({ ...rest });
		const content = formatWorkerApplicantList(res.data.content);

		yield put(
			WorkerApplicantActions.getWorkerApplicantListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getWorkerApplicantListFailure());
	}
}

export function* getWorkerApplicantExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantExcelListRequest>
) {
	const { callback, pathname, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantListResponse> =
			pathname === ROUTERS.ED1_01
				? yield API.getWorkerDashboardList({ ...rest })
				: yield API.getWorkerApplicantList({ ...rest });
		const content = formatWorkerExcelApplicantList(res.data.content);

		yield put(WorkerApplicantActions.getWorkerApplicantExcelListSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerApplicantActions.getWorkerApplicantExcelListFailure());
	}
}

export function* getWorkerApplicantAssignmentListSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantAssignmentListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantAssignmentListResponse> =
			yield API.getWorkerApplicantAssignmentList({ ...rest });
		const content = formatWorkerApplicantAssignmentList(res?.data?.content);
		yield put(
			WorkerApplicantActions.getWorkerApplicantAssignmentListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.({ total: res?.data?.total, data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerApplicantActions.getWorkerApplicantAssignmentListFailure());
	}
}

export function* getWorkerApplicantProgramListSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantProgramListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantProgramListResponse> =
			yield API.getWorkerApplicantProgramList({ ...rest });

		const content = formatWorkerApplicantProgramList(res.data.content);
		yield put(
			WorkerApplicantActions.getWorkerApplicantProgramListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getWorkerApplicantProgramListFailure());
	}
}

export function* getWorkerApplicantProcessInfoListSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantProcessInfoListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantProcessInfoListResponse> =
			yield API.getWorkerApplicantProcessInfoList({ ...rest });

		const content = formatWorkerApplicantProcessInfoList(res.data.content);
		yield put(
			WorkerApplicantActions.getWorkerApplicantProcessInfoListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerApplicantActions.getWorkerApplicantProcessInfoListFailure()
		);
	}
}

export function* updateStatusWorkerApplicantSaga(
	action: PayloadAction<IPayloadUpdateStatusWorkerApplicantRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.updateStatusWorkerApplicant(data);

		yield put(WorkerApplicantActions.updateStatusWorkerApplicantSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.updateStatusWorkerApplicantFailure());
	}
}

export function* updateExtensionPeriodSaga(
	action: PayloadAction<IPayloadUpdateExtensionPeriodRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.updateExtensionPeriod(data);

		yield put(WorkerApplicantActions.updateExtensionPeriodSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.updateExtensionPeriodFailure());
	}
}

export function* createWorkerApplicantSaga(
	action: PayloadAction<IPayloadCreateWorkerApplicantRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		yield API.createWorkerApplicant({
			...props,
		});

		yield put(WorkerApplicantActions.createWorkerApplicantSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.createWorkerApplicantFailure());
	}
}

export function* createWorkerApplicantProgramSaga(
	action: PayloadAction<IPayloadCreateWorkerApplicantProgramRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		yield API.createWorkerApplicantProgram({
			...props,
		});

		yield put(WorkerApplicantActions.createWorkerApplicantProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.createWorkerApplicantProgramFailure());
	}
}

export function* createWorkerApplicantLodgingSaga(
	action: PayloadAction<IPayloadCreateWorkerApplicantLodgingRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		yield API.createWorkerApplicantLodging({
			...props,
		});

		yield put(WorkerApplicantActions.createWorkerApplicantLodgingSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.createWorkerApplicantLodgingFailure());
	}
}

export function* deleteWorkerApplicantsSaga(
	action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerApplicant(ids);

		yield put(WorkerApplicantActions.deleteWorkerApplicantSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.deleteWorkerApplicantFailure());
	}
}

export function* deleteWorkerApplicantsAssignmentSaga(
	action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerApplicantAssignment(ids);

		yield put(WorkerApplicantActions.deleteWorkerApplicantAssignmentSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.deleteWorkerApplicantAssignmentFailure());
	}
}

export function* deleteWorkerApplicantsProgramSaga(
	action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerApplicantProgram(ids);

		yield put(WorkerApplicantActions.deleteWorkerApplicantProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.deleteWorkerApplicantProgramFailure());
	}
}

export function* deleteWorkerApplicantLodgingSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { callback, id } = action.payload;

	try {
		yield API.deleteWorkerApplicantLodging(id);

		yield put(WorkerApplicantActions.deleteWorkerApplicantLodgingSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.deleteWorkerApplicantLodgingFailure());
	}
}

export function* deleteWorkerApplicantProcessInfosSaga(
	action: PayloadAction<IPayloadDeleteWorkerApplicantProcessInfoRequest>
) {
	const { callback, body, id } = action.payload;

	try {
		yield API.deleteWorkerApplicantProcessInfo({ id, body });

		yield put(WorkerApplicantActions.deleteWorkerApplicantProcessInfoSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerApplicantActions.deleteWorkerApplicantProcessingInfoFailure()
		);
	}
}

export function* getWorkerApplicantDetailSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerApplicantDetailResponse> =
			yield API.getWorkerApplicantDetail(id);

		const workerApplicantDetail = formatWorkerApplicantDetail(res.data);

		yield put(
			WorkerApplicantActions.getWorkerApplicantDetailSuccess({
				workerApplicantDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getWorkerApplicantDetailFailure());
	}
}

export function* getWorkerApplicantRecommenderSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TApplicantRecommender> =
			yield API.getWorkerApplicantRecommender(id);

		const content = formatWorkerApplicantRecommender(res.data);

		yield put(
			WorkerApplicantActions.getWorkerApplicantRecommenderSuccess({
				workerApplicantRecommender: content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getWorkerApplicantRecommenderFailure());
	}
}

export function* updateWorkerApplicantRecommenderSaga(
	action: PayloadAction<IPayloadUpdateWorkerApplicantRecommenderRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.updateWorkerApplicantRecommender(data);

		yield put(WorkerApplicantActions.updateWorkerApplicantRecommenderSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.updateWorkerApplicantRecommenderFailure());
	}
}

export function* getWorkerApplicantLodgingSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TApplicantLodging> =
			yield API.getWorkerApplicantLodging(id);
		const content = formatWorkerApplicantLodging(res.data, id);
		yield put(
			WorkerApplicantActions.getWorkerApplicantLodgingSuccess({
				workerApplicantLodging: content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getWorkerApplicantLodgingFailure());
	}
}

export function* getWorkerApplicantLodgingAutoFillSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TApplicantLodgingAutoFill> =
			yield API.getWorkerApplicantLodgingAutoFill(id);

		yield put(
			WorkerApplicantActions.getWorkerApplicantLodgingAutoFillSuccess({
				workerApplicantLodgingAutoFill: res.data,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerApplicantActions.getWorkerApplicantLodgingAutoFillFailure()
		);
	}
}

export function* uploadExcelApplicantSaga(
	action: PayloadAction<TPayloadUploadExcelApplicantRequest>
) {
	const { data, callback, customFields, hasAlert62, errorList } =
		action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelApplicantResponse> =
			yield API.uploadExcelApplicant(data, hasAlert62 ? errorList : []);

		const dataMyPage: ReturnType<typeof MyPageSelectors.selectDataMyPage> =
			yield select(MyPageSelectors.selectDataMyPage);
		const userInfo: API.UploadExcelUserInfo = {
			districtId: dataMyPage?.districtId,
			districtName: dataMyPage?.districtName,
			role: dataMyPage?.role,
		};

		const failedList = formatUploadExcelApplicantFailedList({
			input: res?.data,
			customFields,
			userInfo,
		});
		const successCount = data?.length - (failedList?.length || 0);
		const failCount = failedList?.length || 0;

		yield put(
			WorkerApplicantActions.uploadExcelApplicantSuccess({
				failedList,
				successCount,
				failCount,
				hasAlert62,
			})
		);
		let hasAlert62Arr: (API.TUploadExcelApplicantResponse[number]['data'] & {
			shouldDeleteAssignment: boolean;
		})[] = [];
		let newErrorList: API.TUploadExcelApplicantFailedItem[] = [];

		res?.data?.forEach((item) => {
			if (
				item?.errorList?.length === 1 &&
				item?.errorList?.[0]?.reason === UploadExcelError.EXCEL_AR_11
			) {
				hasAlert62Arr.push({
					...item?.data,
					shouldDeleteAssignment: true,
				});
			} else {
				newErrorList.push(item);
			}
		});
		callback?.({
			failCount,
			successCount,
			hasAlert62Arr,
			errorList: newErrorList,
		});
	} catch (error: any) {
		callback?.({
			error,
			failCount: 0,
			successCount: 0,
		});
		yield put(WorkerApplicantActions.uploadExcelApplicantFailure());
	}
}

export function* canAssignApplicantSaga(
	action: PayloadAction<IPayloadDeleteWorkerApplicantRequest>
) {
	const { callback, ids } = action.payload;

	try {
		const res: API.ApiResponseSuccess<number> =
			yield API.canAssignApplicant(ids);

		yield put(
			WorkerApplicantActions.canAssignApplicantSuccess({
				publicFranchiseId: res.data,
			})
		);
		callback?.({ data: res.data });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerApplicantActions.canAssignApplicantFailure());
	}
}

export function* getApplicantNameplateSaga(
	action: PayloadAction<IPayloadApplicantCardRequest>
) {
	const { ids, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IApplicantNameplate> =
			yield API.getApplicantNameplate(ids);

		yield put(WorkerApplicantActions.getApplicantNameplateSuccess(res.data));
		callback?.(null, res.data);
	} catch (error) {
		callback?.(error);
		yield put(WorkerApplicantActions.getApplicantNameplateFailed());
	}
}

export function* getWorkerApplicantDepartureDateSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDepartureDateRequest>
) {
	const { callback, ...data } = action.payload;

	try {
		const res: API.ApiResponseSuccess<IPayloadGetWorkerApplicantDepartureDateResponse> =
			yield API.getWorkerApplicantDepartureDate(data);

		callback?.(undefined, res.data);
	} catch (error) {
		callback?.(error, undefined);
	}
}
